import { useState } from 'react';
import { availableFontWeight, FONT_REGISTRY } from '../fontList';
import { useEditor } from '../../state/editorStore';
import { StudioCaptionLines } from './StudioCaptionLines';

/**
 * Edit style tab - customize the applied template for the whole project. Writes
 * the project's style/placement overrides (updateStyle / setPlacement), so it
 * updates the live preview instantly, is undoable, and persists + exports. Four
 * sub-tabs: Text, Lines (per-row looks), Color, Position.
 */
const ANCHORS = ['top', 'upper', 'center', 'lower', 'bottom'] as const;
const CASES: { v: 'none' | 'uppercase' | 'lowercase'; label: string }[] = [
  { v: 'none', label: 'Aa' },
  { v: 'uppercase', label: 'AA' },
  { v: 'lowercase', label: 'aa' },
];
const ALIGNS: { v: 'left' | 'center' | 'right'; label: string }[] = [
  { v: 'left', label: 'Left' },
  { v: 'center', label: 'Center' },
  { v: 'right', label: 'Right' },
];
const BG_MODES: { v: 'none' | 'block' | 'perLine'; label: string }[] = [
  { v: 'none', label: 'None' },
  { v: 'block', label: 'Bar' },
  { v: 'perLine', label: 'Per line' },
];
const SAFE_ZONES: { v: 'off' | 'youtube' | 'instagram'; label: string }[] = [
  { v: 'off', label: 'Hide' },
  { v: 'youtube', label: 'YT' },
  { v: 'instagram', label: 'Insta' },
];
const WORDS_PER_GROUP: { v: 'auto' | '1' | '2' | '3' | '4' | '5'; label: string }[] = [
  { v: 'auto', label: 'Auto' },
  { v: '1', label: '1' },
  { v: '2', label: '2' },
  { v: '3', label: '3' },
  { v: '4', label: '4' },
  { v: '5', label: '5' },
];

function hex(value: string | undefined, fallback: string): string {
  if (value && /^#[0-9a-fA-F]{6}$/.test(value)) return value;
  return fallback;
}

export function StudioEditStylePanel() {
  const style = useEditor((s) => s.resolvedStyle());
  const placement = useEditor((s) => s.resolvedPlacement());
  const updateStyle = useEditor((s) => s.updateStyle);
  const setPlacement = useEditor((s) => s.setPlacement);
  const reset = useEditor((s) => s.resetStyle);
  const wordsPerGroup = useEditor((s) => s.wordsPerGroup);
  const setWordsPerGroup = useEditor((s) => s.setWordsPerGroup);
  const safeZone = useEditor((s) => s.safeZone);
  const setSafeZone = useEditor((s) => s.setSafeZone);

  const [tab, setTab] = useState<'text' | 'lines' | 'color' | 'position'>('text');
  const font = FONT_REGISTRY.find((f) => f.family === style.fontFamily);
  const availableWeights = font?.weights ?? [style.fontWeight];
  const renderedWeight = availableFontWeight(style.fontFamily, style.fontWeight);
  const bgOn = style.background.mode !== 'none';
  const strokeOn = !!style.stroke && style.stroke.widthPx > 0;

  return (
    <div className="slp-body shk">

      <div className="shk-subtabs">
        {(['text', 'lines', 'color', 'position'] as const).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'lines' && <StudioCaptionLines />}

      {tab === 'text' && (
        <div className="shk-fields">
          <Row label="Font">
            <select
              className="ses-select"
              value={style.fontFamily}
              onChange={(e) => updateStyle({ fontFamily: e.target.value, fontWeight: availableFontWeight(e.target.value, style.fontWeight) })}
            >
              {FONT_REGISTRY.map((f) => (
                <option key={f.family} value={f.family}>
                  {f.displayName}
                </option>
              ))}
            </select>
          </Row>
          <Slider label="Size" min={30} max={140} step={1} value={style.fontSizePx} suffix=""
            onChange={(v) => updateStyle({ fontSizePx: v })} display={`${Math.round(style.fontSizePx)}`} />
          <Slider label="Weight" min={0} max={Math.max(1, availableWeights.length - 1)} step={1}
            value={Math.max(0, availableWeights.indexOf(renderedWeight))}
            disabled={availableWeights.length === 1}
            onChange={(index) => updateStyle({ fontWeight: availableWeights[index]! })}
            display={`${renderedWeight}`} />
          {renderedWeight !== style.fontWeight && (
            <p className="shk-hint">{style.fontFamily} renders the saved weight {style.fontWeight} as {renderedWeight}. Move the slider above to update it.</p>
          )}
          <Slider label="Line height" min={0.9} max={1.8} step={0.05} value={style.lineHeight}
            onChange={(v) => updateStyle({ lineHeight: v })} display={style.lineHeight.toFixed(2)} />
          <Slider label="Letter spacing" min={-4} max={20} step={0.5} value={style.letterSpacingPx}
            onChange={(v) => updateStyle({ letterSpacingPx: v })} display={`${style.letterSpacingPx}`} />
          <Row label="Case">
            <Seg options={CASES} value={style.textTransform === 'capitalize' ? 'none' : style.textTransform}
              onPick={(v) => updateStyle({ textTransform: v })} />
          </Row>
          <Row label="Align">
            <Seg options={ALIGNS} value={style.textAlign} onPick={(v) => updateStyle({ textAlign: v })} />
          </Row>
          <div className="ses-divider" />
          <Row label="Reveal as spoken">
            <Toggle on={style.revealMode === 'word'}
              onClick={() => updateStyle({ revealMode: style.revealMode === 'word' ? 'segment' : 'word' })} />
          </Row>
          <Row label="Words at a time">
            <Seg options={WORDS_PER_GROUP} value={wordsPerGroup == null ? 'auto' : String(wordsPerGroup)}
              onPick={(v) => setWordsPerGroup(v === 'auto' ? null : Number(v))} />
          </Row>
        </div>
      )}

      {tab === 'color' && (
        <div className="shk-fields">
          <Row label="Text color">
            <input type="color" className="shk-color" value={hex(style.color, '#ffffff')}
              onChange={(e) => updateStyle({ color: e.target.value })} />
          </Row>
          <Row label="Highlight spoken word">
            <Toggle on={style.activeWordHighlight !== false}
              onClick={() => updateStyle({ activeWordHighlight: style.activeWordHighlight === false })} />
          </Row>
          {style.activeWordHighlight !== false && (
            <Row label="Spoken word color">
              <input type="color" className="shk-color" value={hex(style.activeWord.color, '#ffd400')}
                onChange={(e) => updateStyle({ activeWord: { color: e.target.value } })} />
            </Row>
          )}
          <Row label="Animate spoken word">
            <Toggle on={style.activeWordMotion !== false}
              onClick={() => updateStyle({ activeWordMotion: style.activeWordMotion === false })} />
          </Row>
          <div className="ses-divider" />
          <Row label="Background">
            <Seg options={BG_MODES} value={style.background.mode === 'perLine' ? 'perLine' : style.background.mode === 'none' ? 'none' : 'block'}
              onPick={(v) =>
                updateStyle({
                  background: v === 'none' ? { mode: 'none' } : { mode: v, color: style.background.color ?? '#101216' },
                })
              } />
          </Row>
          {bgOn && (
            <>
              <Row label="Background color">
                <input type="color" className="shk-color" value={hex(style.background.color, '#101216')}
                  onChange={(e) => updateStyle({ background: { color: e.target.value } })} />
              </Row>
              <Slider label="Corner radius" min={0} max={40} step={1} value={style.background.radiusPx}
                onChange={(v) => updateStyle({ background: { radiusPx: v } })} display={`${Math.round(style.background.radiusPx)}`} />
            </>
          )}
          <div className="ses-divider" />
          <Row label="Outline">
            <Toggle on={strokeOn}
              onClick={() =>
                updateStyle({ stroke: { color: style.stroke?.color ?? '#000000', widthPx: strokeOn ? 0 : 6 } })
              } />
          </Row>
          {strokeOn && (
            <>
              <Row label="Outline color">
                <input type="color" className="shk-color" value={hex(style.stroke?.color, '#000000')}
                  onChange={(e) => updateStyle({ stroke: { color: e.target.value, widthPx: style.stroke?.widthPx ?? 6 } })} />
              </Row>
              <Slider label="Outline width" min={1} max={20} step={1} value={style.stroke?.widthPx ?? 6}
                onChange={(v) => updateStyle({ stroke: { color: style.stroke?.color ?? '#000000', widthPx: v } })} display={`${Math.round(style.stroke?.widthPx ?? 6)}`} />
            </>
          )}
        </div>
      )}

      {tab === 'position' && (
        <div className="shk-fields">
          <Row label="Vertical">
            <div className="ses-anchors">
              {ANCHORS.map((a) => (
                <button key={a} className={placement.anchor === a && placement.yNorm === 0 ? 'on' : ''}
                  onClick={() => setPlacement({ anchor: a, yNorm: 0 })}>
                  {a}
                </button>
              ))}
            </div>
          </Row>
          <Slider label="Fine Y" min={-0.3} max={0.3} step={0.01} value={placement.yNorm}
            onChange={(v) => setPlacement({ yNorm: v })} display={`${placement.yNorm > 0 ? '+' : ''}${Math.round(placement.yNorm * 100)}%`} />
          <Slider label="Horizontal" min={0} max={1} step={0.01} value={placement.xNorm}
            onChange={(v) => setPlacement({ xNorm: v })} display={`${Math.round(placement.xNorm * 100)}%`} />
          <Slider label="Caption width" min={0.4} max={1} step={0.02} value={placement.maxWidthNorm}
            onChange={(v) => setPlacement({ maxWidthNorm: v })} display={`${Math.round(placement.maxWidthNorm * 100)}%`} />
          <div className="ses-divider" />
          <Row label="Safe zones">
            <Seg options={SAFE_ZONES} value={safeZone} onPick={setSafeZone} />
          </Row>
          <Slider label="Animation" min={0} max={2} step={0.1} value={style.entrance.intensity}
            onChange={(v) => updateStyle({ entrance: { intensity: v }, activeWord: { animation: { intensity: v } } })}
            display={style.entrance.intensity.toFixed(1)} />
          <button className="shk-reset" onClick={reset}>
            Reset to template defaults
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="shk-row">
      <span>{label}</span>
      {children}
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, display, suffix, disabled }: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void; display: string; suffix?: string; disabled?: boolean;
}) {
  return (
    <div className="shk-row">
      <span>{label}</span>
      <div className="shk-size">
        <input type="range" aria-label={label} aria-valuetext={display} disabled={disabled}
          min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))} />
        <span>{display}{suffix}</span>
      </div>
    </div>
  );
}

function Seg<T extends string>({ options, value, onPick }: {
  options: { v: T; label: string }[]; value: T; onPick: (v: T) => void;
}) {
  return (
    <div className="ses-seg">
      {options.map((o) => (
        <button key={o.v} className={value === o.v ? 'on' : ''} onClick={() => onPick(o.v)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button className={on ? 'shk-toggle on' : 'shk-toggle'} onClick={onClick} aria-pressed={on}>
      <span className="shk-toggle-knob" />
    </button>
  );
}
